import { mapIntegrationProviderFromDTO } from '@/modules/common/mappers/integration.mapper';

import { TeamMemberChangeMetadataResponse } from '../types/dto/response/team-member-change-metadata-response';
import {
  TeamMemberChangedFieldResponse,
  TeamMemberFieldNameDTO,
} from '../types/dto/team-member-changed-field.dto';
import {
  TeamMemberChangedBy,
  TeamMemberChangedField,
  TeamMemberChangeLogEntry,
} from '../types/team-member-changes';
import { mapTeamMemberChangeSectionFromDTO } from './team-member-change-section.mapper';

const INTEGRATION_FIELD_NAMES = new Set<string>([
  TeamMemberFieldNameDTO.GOOGLE,
  TeamMemberFieldNameDTO.JIRA,
  TeamMemberFieldNameDTO.QUICK_BOOKS,
  TeamMemberFieldNameDTO.TEMPO,
  TeamMemberFieldNameDTO.CLOCKIFY,
  TeamMemberFieldNameDTO.BAMBOOHR,
]);

/**
 * Maps a single changed field from response to domain model.
 * For integration fields, fieldLocalizedName contains the connected account name
 * (e.g. "John Doe" for a Google account). We use that name as the display value
 * instead of the raw numeric ID stored in oldValue/newValue.
 */
const mapChangedFieldFromResponse = (
  fieldResponse: TeamMemberChangedFieldResponse
): TeamMemberChangedField => {
  const isIntegrationField = INTEGRATION_FIELD_NAMES.has(fieldResponse.fieldName);
  const accountName = fieldResponse.fieldLocalizedName ?? null;

  if (isIntegrationField && accountName) {
    return {
      field: fieldResponse.fieldName,
      // Clear fieldLocalizedName so the label renders as the integration type name ("Google", etc.)
      fieldLocalizedName: null,
      // Backend sends names for old values directly in oldValue, so keep it as-is.
      // fieldLocalizedName carries the name for the new connection — use it instead of the raw ID.
      from: fieldResponse.oldValue ?? null,
      to: fieldResponse.newValue != null ? accountName : null,
      subfieldChanges: fieldResponse.subfieldChanges
        ? fieldResponse.subfieldChanges.map(mapChangedFieldFromResponse)
        : null,
    };
  }

  return {
    field: fieldResponse.fieldName,
    fieldLocalizedName: fieldResponse.fieldLocalizedName ?? null,
    from: fieldResponse.oldValue ?? null,
    to: fieldResponse.newValue ?? null,
    subfieldChanges: fieldResponse.subfieldChanges
      ? fieldResponse.subfieldChanges.map(mapChangedFieldFromResponse)
      : null,
  };
};

/**
 * Maps changed fields from response, grouping fields with same name
 */
const mapChangedFieldsFromResponse = (
  fieldsResponse: TeamMemberChangedFieldResponse[]
): TeamMemberChangedField[] => {
  const mappedFields = fieldsResponse.map(mapChangedFieldFromResponse);

  // Group fields with same field name
  const groupedMap = new Map<string, TeamMemberChangedField[]>();

  for (const field of mappedFields) {
    const existing = groupedMap.get(field.field);
    if (existing) {
      existing.push(field);
    } else {
      groupedMap.set(field.field, [field]);
    }
  }

  const result: TeamMemberChangedField[] = [];

  for (const [fieldName, fields] of groupedMap) {
    if (fields.length > 1) {
      // Group multiple fields of same type
      result.push({
        field: fieldName,
        fieldLocalizedName: fields[0].fieldLocalizedName,
        from: null,
        to: null,
        subfieldChanges: fields,
        isGrouped: true,
        groupCount: fields.length,
      });
    } else {
      result.push(fields[0]);
    }
  }

  return result;
};

const mapTeamMemberChangedBy = (
  response: TeamMemberChangeMetadataResponse
): TeamMemberChangedBy => {
  if (response.changedByIntegration) {
    return {
      id: 0,
      name: mapIntegrationProviderFromDTO(response.changedByIntegration),
      profileImageUrl: null,
      isIntegration: true,
      integrationProvider: mapIntegrationProviderFromDTO(response.changedByIntegration),
    };
  }

  if (response.changedByUser) {
    return {
      id: response.changedByUser.id,
      name: response.changedByUser.name,
      profileImageUrl: response.changedByUser.profileImageUrl ?? null,
      isIntegration: false,
    };
  }

  return {
    id: 0,
    name: 'Unknown',
    profileImageUrl: null,
    isIntegration: false,
  };
};

export const mapTeamMemberChangeFromResponse = (
  response: TeamMemberChangeMetadataResponse
): TeamMemberChangeLogEntry => {
  return {
    id: response.id,
    type: mapTeamMemberChangeSectionFromDTO(response.section),
    startDate: response.effectiveStartDate,
    endDate: response.effectiveEndDate,
    changedBy: mapTeamMemberChangedBy(response),
    changedFields: mapChangedFieldsFromResponse(response.changedFields),
    allowedActions: response.allowedActions,
    isActive: response.isActive,
    isInitialChange: response.isInitialChange,
  };
};

export const mapTeamMemberChangesFromResponse = (
  responses: TeamMemberChangeMetadataResponse[]
): TeamMemberChangeLogEntry[] => {
  return responses.map(mapTeamMemberChangeFromResponse);
};
